import React, { useState, useEffect } from "react";
import { MessageCircle, ArrowRight } from "lucide-react";
import { buildWhatsappUrl } from "../data/conteudoPorNicho";
import RoboAnimado from "./RoboAnimado";
import BaloesCanais from "./BaloesCanais";

// -----------------------------------------------------------------------------
// COPY DOS 2 ESTAGIOS DO HERO
// Ajuste livre. Um timer interno alterna entre estagio1 e estagio2 a cada
// DURACAO_ESTAGIO_MS. O robo continua so em bracos cruzados, independente daqui.
// -----------------------------------------------------------------------------
const COPY = {
  estagio1: {
    badge: "Um sistema, todos os canais",
    titulo: "Seu negócio conectado em todos os canais",
    subtitulo:
      "WhatsApp, Instagram, Messenger e Telegram no mesmo painel. Mais IA, automação e gestão completa.",
  },
  estagio2: {
    badge: "Comece agora",
    titulo: "Comece hoje, sem cartão, sem fidelidade",
    subtitulo:
      "Plano gratuito pra sempre. Faça um teste e veja como o Evolzap se encaixa no seu negócio.",
  },
};

const DURACAO_ESTAGIO_MS = 5000;

const HERO_CTA = {
  ctaPrimarioLabel: "Falar no WhatsApp",
  ctaSecundarioLabel: "Ver planos",
  mensagemWhatsapp: "Olá! Quero saber mais sobre o Evolzap",
  provaSocial: "+200 negócios",
  provaSocialSuffix: "usando o Evolzap",
};

export default function Hero() {
  const [estagio, setEstagio] = useState("estagio1");

  // Timer que alterna entre estagio1 e estagio2 a cada DURACAO_ESTAGIO_MS.
  // Ciclo total = 2 * DURACAO_ESTAGIO_MS (10s), sincronizado com o loop dos baloes.
  useEffect(() => {
    const interval = setInterval(() => {
      setEstagio((atual) => (atual === "estagio1" ? "estagio2" : "estagio1"));
    }, DURACAO_ESTAGIO_MS);
    return () => clearInterval(interval);
  }, []);

  const whatsappHref = buildWhatsappUrl(HERO_CTA.mensagemWhatsapp);
  const copyAtual = COPY[estagio] || COPY.estagio1;

  return (
    <section
      id="topo"
      className="relative overflow-hidden pt-[42px] md:pt-[74px] pb-8 md:pb-12 px-4 md:px-6"
      data-testid="hero"
    >
      {/* Blob decorativo do icon */}
      <div
        className="pointer-events-none absolute -top-20 -right-20 md:-right-10 w-[600px] h-[600px] opacity-[0.07] blur-3xl"
        aria-hidden="true"
      >
        <img
          src="/evolzap-icon.png"
          alt=""
          className="w-full h-full object-contain"
        />
      </div>

      {/* Gradient sutil verde */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[600px] bg-gradient-to-b from-green-500/[0.06] via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Coluna esquerda — mensagem sincronizada com a fase */}
        <div className="flex flex-col gap-6 md:gap-7">
          {/* Badge (troca com fade) */}
          <div
            key={`badge-${estagio}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full self-start animate-fade-in-up"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-xs font-medium text-green-400">{copyAtual.badge}</span>
          </div>

          {/* Titulo (troca com fade + slide) */}
          <h1
            key={`titulo-${estagio}`}
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05] animate-fade-in-up"
            data-testid="hero-titulo"
          >
            {copyAtual.titulo}
          </h1>

          {/* Subtitulo (troca com fade + slide) */}
          <p
            key={`sub-${estagio}`}
            className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-xl animate-fade-in-up"
          >
            {copyAtual.subtitulo}
          </p>

          {/* Baloes das integracoes + texto complementar (aparecem no estagio1, somem no fim do estagio2) */}
          <div className="flex flex-col gap-2 mt-1">
            <BaloesCanais estagio={estagio} />
            <p className="text-sm text-zinc-500">
              CRM completo com IA e automação
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-1">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-black font-semibold px-6 py-3.5 rounded-xl shadow-lg shadow-green-500/20 hover:scale-[1.02] transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              {HERO_CTA.ctaPrimarioLabel}
            </a>
            <a
              href="#planos"
              data-testid="hero-cta-planos"
              className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-6 py-3.5 rounded-xl transition-all"
            >
              {HERO_CTA.ctaSecundarioLabel}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <div className="flex -space-x-2">
              {[
                "from-green-500 to-emerald-600",
                "from-blue-500 to-indigo-600",
                "from-green-400 to-teal-500",
                "from-blue-600 to-violet-600",
              ].map((g, i) => (
                <span
                  key={i}
                  className={`w-8 h-8 rounded-full border-2 border-[#0A0A0A] bg-gradient-to-br ${g}`}
                />
              ))}
            </div>
            <p className="text-sm text-zinc-400">
              <span className="text-white font-semibold">{HERO_CTA.provaSocial}</span>{" "}
              {HERO_CTA.provaSocialSuffix}
            </p>
          </div>
        </div>

        {/* Coluna direita — mascote animado (desktop) */}
        <div
          className="hidden md:block relative animate-fade-in-up"
          style={{ animationDelay: "150ms" }}
        >
          <RoboAnimado />
        </div>
      </div>
    </section>
  );
}
